import { normalizeLanguageCode } from './i18n'

export const PUBLIC_LOCALES = ['da', 'en', 'de', 'es', 'fr', 'it', 'nl', 'pl', 'pt', 'se'] as const

export type PublicLocale = (typeof PUBLIC_LOCALES)[number]

const LOCALE_TO_LANG: Record<PublicLocale, string> = {
  da: 'DA',
  en: 'EN',
  de: 'DE',
  es: 'ES',
  fr: 'FR',
  it: 'IT',
  nl: 'NL',
  pl: 'PL',
  pt: 'PT',
  se: 'SE',
}

export function isPublicLocale(value: string | null | undefined): value is PublicLocale {
  return !!value && PUBLIC_LOCALES.includes(value.toLowerCase() as PublicLocale)
}

export function localeToAppLang(locale: string | null | undefined): string {
  if (!isPublicLocale(locale)) return 'EN'
  return normalizeLanguageCode(LOCALE_TO_LANG[locale])
}

export function appLangToLocale(lang: string | null | undefined): PublicLocale {
  const normalized = normalizeLanguageCode(lang || 'EN')
  const pair = Object.entries(LOCALE_TO_LANG).find(([, code]) => code === normalized)
  return (pair?.[0] as PublicLocale) || 'en'
}

export function splitLocalizedPath(pathname: string): { locale: PublicLocale | null; basePath: string } {
  const parts = pathname.split('/').filter(Boolean)
  const first = parts[0]?.toLowerCase()
  if (!isPublicLocale(first)) return { locale: null, basePath: pathname || '/' }
  const rest = parts.slice(1).join('/')
  return { locale: first, basePath: `/${rest}`.replace(/\/+$/, '') || '/' }
}

export function withLocalePath(path: string, locale: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  if (!isPublicLocale(locale)) return normalizedPath
  if (normalizedPath === '/') return `/${locale}`
  return `/${locale}${normalizedPath}`
}
